/**
 * <brands-index> - enhances the server-rendered all-brands page (VT-11b).
 *
 * No-JS state = the complete page: every brand in the A-Z, every logo in the wall, alphabet tiles
 * working as ordinary in-page anchors. The category buttons are the only inert part, and they stay
 * inert (buttons, not links) rather than pretending to navigate somewhere the design has no room
 * for. On upgrade they become a client-side filter:
 * - picking a category hides every brand and logo outside it, hides letter blocks left empty and
 *   disables their tiles, and hides the whole Popular block - heading included - below 3 logos
 *   (BUILD-SPEC ruling 6);
 * - the "All brands" row clears the filter and is the resting state, active whenever nothing is
 *   filtered; re-clicking the active category clears too, as a shortcut rather than the only way
 *   back (frames C/D carried no reset control at all — owner-added at preview round 1);
 * - a category that matches nothing fills the live region instead of leaving a blank pane.
 * Alphabet tiles keep their native anchor behaviour - the jump, the hash and the back button are
 * the browser's, not ours - and only gain aria-current so the picked letter reads as selected.
 * No page reload, no history writes, no fetches.
 *
 * Each category button carries its own vendor set in data-vendors, so there is no id-keyed map to
 * keep in sync with the markup: the button IS the filter's identity.
 *
 * Markup contract: sections/main-brands-index.liquid + snippets/brands-index-letter-group.liquid.
 */
(() => {
  if (customElements.get('brands-index')) return;

  /** Below this many matching logos an active filter hides the Popular block (BUILD-SPEC ruling 6). */
  const MIN_POPULAR_LOGOS = 3;

  /** @param {HTMLElement} button @returns {Set<string>} handleized vendor names for one category. */
  const vendorsOf = (button) => new Set((button.dataset.vendors || '').split(' ').filter(Boolean));

  class BrandsIndex extends HTMLElement {
    #controller = null;
    #activeButton = null;

    connectedCallback() {
      this.#controller = new AbortController();
      const { signal } = this.#controller;

      this.navButtons = Array.from(this.querySelectorAll('.brands-index__nav-button'));
      this.allButton = this.querySelector('[data-all-brands]');
      this.letterBlocks = Array.from(this.querySelectorAll('.brands-index__letter'));
      this.brandItems = Array.from(this.querySelectorAll('.brands-index__brand'));
      this.popularBlock = this.querySelector('.brands-index__popular');
      this.popularItems = Array.from(this.querySelectorAll('.brands-index__wall-item'));
      this.emptyMessage = this.querySelector('.brands-index__empty');
      if (!this.letterBlocks.length) return;

      // Only letters that had brands at render time shipped as anchors; the rest are disabled
      // spans and stay out of this map, so clearing a filter can never revive them.
      this.tiles = new Map();
      for (const tile of this.querySelectorAll('a.brands-index__tile')) {
        this.tiles.set(tile.dataset.letter, tile);
      }

      // The tiles are ordinary anchors, so the jump, the hash and the back button all belong to
      // the browser; the only thing left to do is keep "selected" in step with the hash — which
      // also makes a shared #BrandsLetter-… link arrive with its letter already highlighted.
      window.addEventListener('hashchange', () => this.#syncTileFromHash(), { signal });
      this.#syncTileFromHash();

      // The rail is desktop-only; below 990px the same buttons render as a scrolling tab strip.
      this.tabStripMQ = window.matchMedia('(max-width: 989px)');

      for (const button of this.navButtons) {
        button.addEventListener('click', () => this.#selectCategory(button), { signal });
      }

      this.setAttribute('data-enhanced', '');
    }

    disconnectedCallback() {
      this.#controller?.abort();
    }

    #selectCategory(button) {
      // "All brands" always clears; any other row clears only when it is already the active one
      // (the re-click path, kept as a shortcut now that there is a visible control as well).
      const clears = button.hasAttribute('data-all-brands') || button === this.#activeButton;
      const next = clears ? null : button;
      this.#activeButton = next;

      // Unfiltered is not "nothing selected": it is "All brands selected", which is what the
      // server renders and what the reset row has to read as once a filter is cleared.
      const current = next || this.allButton;
      for (const item of this.navButtons) {
        if (item === current) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      }

      this.#applyFilter(next ? vendorsOf(next) : null);

      // The strip scrolls horizontally and an active tab is wider than an inactive one, so a tab
      // tapped near either edge can be clipped by its own state change.
      if (next && this.tabStripMQ.matches) {
        next.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    #syncTileFromHash() {
      const hash = window.location.hash;
      for (const tile of this.tiles.values()) {
        // getAttribute, not .href: a disabled tile has no href, and .href would resolve absolute.
        if (hash && tile.getAttribute('href') === hash) tile.setAttribute('aria-current', 'true');
        else tile.removeAttribute('aria-current');
      }
    }

    /** @param {Set<string> | null} vendors - null clears the filter and restores every row. */
    #applyFilter(vendors) {
      for (const item of this.brandItems) {
        item.hidden = vendors !== null && !vendors.has(item.dataset.vendor);
      }

      let visibleBlocks = 0;
      for (const block of this.letterBlocks) {
        const kept = block.querySelector('.brands-index__brand:not([hidden])') !== null;
        block.hidden = !kept;
        if (kept) visibleBlocks += 1;
        this.#setTileEnabled(block.dataset.letter, kept);
      }

      let visibleLogos = 0;
      for (const item of this.popularItems) {
        const show = vendors === null || vendors.has(item.dataset.vendor);
        item.hidden = !show;
        if (show) visibleLogos += 1;
      }
      if (this.popularBlock) {
        this.popularBlock.hidden = vendors !== null && visibleLogos < MIN_POPULAR_LOGOS;
      }

      if (this.emptyMessage) {
        // "Nothing on screen", not "no letter blocks": the wall can survive a filter that empties
        // the A-Z (every match hidden from the listing), and a message contradicting a visible
        // row of logos is worse than no message.
        const wallShown = this.popularBlock !== null && !this.popularBlock.hidden;
        const nothingShown = visibleBlocks === 0 && !wallShown;
        this.emptyMessage.textContent =
          vendors !== null && nothingShown ? this.dataset.emptyText || '' : '';
      }

      // Re-derive "selected" from the hash now that hrefs have been taken away and given back:
      // a tile disabled by this filter drops out, and clearing the filter restores the one the
      // hash still points at. Runs last, because it reads the hrefs #setTileEnabled just wrote.
      this.#syncTileFromHash();
    }

    #setTileEnabled(letter, enabled) {
      const tile = this.tiles.get(letter);
      if (!tile) return;

      if (enabled) {
        if (tile.dataset.href) {
          tile.setAttribute('href', tile.dataset.href);
          delete tile.dataset.href;
        }
        tile.removeAttribute('aria-disabled');
        tile.removeAttribute('role');
        return;
      }

      if (!tile.dataset.href) {
        tile.dataset.href = tile.getAttribute('href');
        tile.removeAttribute('href');
      }
      // An <a> without href falls back to role=generic, which permits no aria-* at all, so
      // aria-disabled alone would be an aria-allowed-attr violation that tells a screen reader
      // nothing. role=link restores the role the attribute belongs to (same pairing as the
      // server-rendered disabled tile). Still unfocusable, so it is never a dead tab stop.
      tile.setAttribute('role', 'link');
      tile.setAttribute('aria-disabled', 'true');
    }
  }

  customElements.define('brands-index', BrandsIndex);
})();
